import { Paper, Group, Text, Badge, Flex, Box, Tooltip, Button } from "@mantine/core";
import { IconShoppingCartDollar, IconDiscountFilled } from "@tabler/icons-react";
import { GpuListing } from "../types/GpuListing";
import { formatPrice } from "../utils/formatters";
import { getRetailerColor, getRetailerDisplayName, getStockBadgeProps } from "../utils/styleHelpers";
import { useTheme } from "../contexts/ThemeContext";

interface ListingCardProps {
  listing: GpuListing;
  onClick: () => void;
}

export const ListingCard = ({ listing, onClick }: ListingCardProps) => {
  const { theme } = useTheme();
  const themeColor = theme === "green" ? "nvidiaGreen" : "amdRed";

  return (
    <Paper
      radius="lg"
      p="md"
      className="listing-card"
      onClick={onClick}
      style={{
        minHeight: 94,
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Desktop Layout */}
      <Group
        gap="md"
        px="md"
        align="center"
        wrap="nowrap"
        style={{ width: "100%" }}
        className="desktop-card-layout"
      >
        <Box style={{ width: "0.25rem" }} />
        <Flex style={{ flex: "0 0 200px", minWidth: 0 }}>
          <Text
            size="md"
            fw={500}
            c="white"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {`${listing.manufacturer} ${listing.variant_name}`}
          </Text>
        </Flex>

        <Flex gap="xs" style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "400px" }} align="center">
          <Text
            size="xs"
            c="dimmed"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {listing.full_name}
          </Text>
        </Flex>

        <Flex style={{ flex: "0 0 140px" }} justify="center">
          <Badge
            color={getRetailerColor(listing.retailer)}
            variant="light"
            size="lg"
            className="hoverable-badge-simple"
          >
            {getRetailerDisplayName(listing.retailer)}
          </Badge>
        </Flex>

        <Box style={{ flex: "0 0 60px" }} />

        <Box style={{ flex: "0 0 120px" }}>
          <Text size="m" fw={listing.price < 7500 ? 700 : 500} c="white">
            {formatPrice(listing.price)}
          </Text>
          {listing.original_price > 0 &&
            listing.original_price !== listing.price && (
              <Group gap={4}>
                <Text size="xs" c="green" fw={500}>
                  -{(listing.original_price - listing.price).toLocaleString("sv-SE")} kr
                </Text>
                <IconDiscountFilled size={14} color="var(--mantine-color-green-filled)" />
              </Group>
            )}
        </Box>

        <Flex style={{ flex: "0 0 120px" }} justify="center">
          <Badge
            color={getStockBadgeProps(listing.stock_status).color}
            variant="outline"
            size="md"
            c={getStockBadgeProps(listing.stock_status).textColor}
            className="hoverable-badge-simple"
          >
            {getStockBadgeProps(listing.stock_status).text}
          </Badge>
        </Flex>

        <Flex style={{ flex: "0 0 80px" }} align="center" justify="center">
          <Tooltip label="View in store" position="top">
            <Button
              component="a"
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              color={themeColor}
              p="xs"
            >
              <IconShoppingCartDollar size={18} />
            </Button>
          </Tooltip>
        </Flex>
      </Group>

      {/* Mobile Layout - 2x2 Grid */}
      <Box style={{ width: "100%" }} className="mobile-card-layout">
        <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
          <Group gap="xs" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Text
              size="sm"
              fw={500}
              c="white"
              className="mobile-brand-model-name"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {`${listing.manufacturer} ${listing.variant_name}`}
            </Text>
          </Group>
          <Group gap="xs" align="center" wrap="nowrap">
            {listing.original_price > 0 &&
              listing.original_price !== listing.price && (
                <Group gap={4}>
                  <Text size="xs" c="green" fw={500}>
                    -{(listing.original_price - listing.price).toLocaleString("sv-SE")} kr
                  </Text>
                  <IconDiscountFilled size={14} color="var(--mantine-color-green-filled)" />
                </Group>
              )}
            <Text size="m" fw={listing.price < 7500 ? 700 : 500} c="white">
              {formatPrice(listing.price)}
            </Text>
          </Group>
        </Group>

        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs">
            <Badge
              color={getRetailerColor(listing.retailer)}
              variant="light"
              size="md"
              className="hoverable-badge-simple"
            >
              {getRetailerDisplayName(listing.retailer)}
            </Badge>
            <Badge
              color={getStockBadgeProps(listing.stock_status).color}
              variant="light"
              size="xs"
              c="dimmed"
              className="hoverable-badge-simple"
            >
              {getStockBadgeProps(listing.stock_status).text}
            </Badge>
          </Group>
          <Tooltip label="View in store" position="top">
            <Button
              component="a"
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              color={themeColor}
              p="xs"
              onClick={(e) => e.stopPropagation()}
            >
              <IconShoppingCartDollar size={18} />
            </Button>
          </Tooltip>
        </Group>
      </Box>
    </Paper>
  );
};
